import React from "react";

import Label from "@busbud/design-system-components/dist/Label";
import Seat from "@busbud/design-system-components/dist/Seat";
import type { SeatBaseState } from "@busbud/design-system-components/dist/SeatBase";
import Stack from "@busbud/design-system-components/dist/Stack";
import type { SeatCell } from "@busbud/int-schemas/udm";

import { LocalizedPrice } from "@app/components/localized-price";
import { useAppContext } from "@app/helpers/hooks";
import { getNormalizedClassNameCapitalized } from "@app/modules/results/utils/classes/normalized-class-name";

import { useStyles } from "./select-to-add-seat-legend.styles";
import { MaxPassengersAlert } from "../components/max-passengers-alert/max-passengers-alert";

const SeatLayout: React.FC<
  React.PropsWithChildren<{
    state?: SeatBaseState;
    seatNumber?: string;
  }>
> = ({ children, state = "available", seatNumber }) => {
  const { classes } = useStyles();

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Seat
        state={state}
        seatNumber={seatNumber}
        classes={{ root: classes.seatIcon }}
      />
      <div className={classes.seatInfoContainer}>{children}</div>
    </Stack>
  );
};

const SelectedSeat: React.FC<{
  display_name?: string;
  class_name: string;
  price_in_cents: number;
  state?: SeatBaseState;
}> = ({ display_name, class_name, price_in_cents, state }) => {
  return (
    <SeatLayout state={state} seatNumber={display_name}>
      <Stack justifyContent="space-between">
        <Stack direction="column" spacing={0.5}>
          <Label size="lg" fontWeight="bold">
            {getNormalizedClassNameCapitalized(class_name)}
          </Label>
          {null}
        </Stack>
        <Label size="lg" fontWeight="bold">
          <LocalizedPrice price_in_cents={price_in_cents} />
        </Label>
      </Stack>
    </SeatLayout>
  );
};

export const SelectToAddSeatLegend: React.FC = () => {
  const { translator } = useAppContext();
  return (
    <SeatLayout>
      <Label color="text.secondary.opaque">
        {translator.t("!results.seat-selection.legend")}
      </Label>
    </SeatLayout>
  );
};

interface PendingSeatsProps {
  count: number;
  class_name: string;
  price_in_cents: number;
}

const PendingSeats: React.FC<PendingSeatsProps> = ({
  count,
  class_name,
  price_in_cents
}) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => {
        return (
          <SelectedSeat
            key={`pending_seat_${index}`}
            class_name={class_name}
            price_in_cents={price_in_cents}
          />
        );
      })}
    </>
  );
};

interface Props {
  seats: SeatCell[];
  class_name: string;
  price_in_cents: number;
  className?: string;
  show_add_seat_legend: boolean;
  count_pending_seats: number;
  show_max_passengers_alert?: boolean;
}

export const SelectedSeats: React.FC<Props> = ({
  seats,
  class_name,
  className,
  price_in_cents,
  show_add_seat_legend,
  count_pending_seats,
  show_max_passengers_alert
}) => {
  return (
    <Stack direction="column" spacing={2} className={className}>
      {!!show_max_passengers_alert && <MaxPassengersAlert />}
      {seats.map(seat => (
        <SelectedSeat
          key={seat.id}
          display_name={seat.display_name}
          class_name={class_name}
          price_in_cents={price_in_cents}
          state="selected"
        />
      ))}
      {count_pending_seats > 0 && (
        <PendingSeats
          count={count_pending_seats}
          class_name={class_name}
          price_in_cents={price_in_cents}
        />
      )}
      {!!show_add_seat_legend && <SelectToAddSeatLegend />}
    </Stack>
  );
};
